import React from 'react';
import { Box, Drawer, Flex } from '@mantine/core';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useCurrentClient } from 'src/clients/context/CurrentClientProvider';
import PageWithSidebar from 'src/UI/page-nav/PageWithSidebar';
import { UpcomingEventsList } from 'src/meetings/home/UpcomingEventsList';
import { useCalendarEvents } from 'src/meetings/hooks/useCalendarEvents';
import { HEADER_HEIGHT } from 'src/UI/app-container/header/Header';
import { EventDetailPanel } from 'src/meetings/home/EventDetailPanel';
import { MeetingDetailsContent } from 'src/meetings/detail/MeetingDetailsContent';

const ClientMeetingsTabs = {
  Upcoming: 'upcoming',
  Past: 'past',
} as const;

export default function ClientMeetingsContent() {
  const { clientId } = useCurrentClient();
  const { sectionSlug, selectedEventId, eventTab } = useParams<{
    sectionSlug: string;
    selectedEventId?: string;
    eventTab?: string;
  }>();
  const pastOrUpcoming = sectionSlug === ClientMeetingsTabs.Past ? 'PAST' : 'UPCOMING';
  const { events, hasNextPage, fetchNextPage, isFetchingNextPage, refetch } = useCalendarEvents({
    pastOrUpcoming,
    clientId,
  });
  const navigate = useNavigate();

  const selectedEvent = events.find(e => e.id === selectedEventId);

  const handlePastEventSelect = (eventId: string | null) => {
    if (!eventId) {
      return;
    }
    navigate(`/client/${clientId}/meetings/past/${eventId}/summary`);
  };

  const handleUpcomingEventSelect = (eventId: string | null) => {
    if (!eventId) {
      return;
    }
    navigate(`/client/${clientId}/meetings/upcoming/${eventId}`);
  };

  const handleUnselectEvent = () => {
    navigate(`/client/${clientId}/meetings/upcoming`);
  };

  const DATA_LINKS = [
    {
      label: 'Upcoming',
      href: `/client/${clientId}/meetings/${ClientMeetingsTabs.Upcoming}`,
    },
    {
      label: 'Past meetings',
      href: `/client/${clientId}/meetings/${ClientMeetingsTabs.Past}`,
    },
  ];
  const content = (() => {
    switch (sectionSlug) {
      case ClientMeetingsTabs.Upcoming:
        return (
          <UpcomingEventsList
            events={events}
            selectedEventId={selectedEventId || null}
            onEventSelect={handleUpcomingEventSelect}
            onRefetchNeeded={refetch}
            hasNextPage={!!hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        );
      case ClientMeetingsTabs.Past:
        return (
          <UpcomingEventsList
            events={events}
            selectedEventId={selectedEventId || null}
            onEventSelect={handlePastEventSelect}
            onRefetchNeeded={refetch}
            hasNextPage={!!hasNextPage}
            fetchNextPage={fetchNextPage}
            isFetchingNextPage={isFetchingNextPage}
          />
        );
      default:
        return (
          <Navigate to={`/client/${clientId}/meetings/${ClientMeetingsTabs.Upcoming}`} replace />
        );
    }
  })();

  return (
    <Flex direction={'row'} justify={'stretch'}>
      <Box flex={1}>
        <Drawer
          opened={!!selectedEventId}
          onClose={handleUnselectEvent}
          withCloseButton={false}
          position="bottom"
          size="md"
          styles={{
            content: {
              top: HEADER_HEIGHT,
              height: `calc(100% - ${HEADER_HEIGHT}px)`,
              display: 'flex',
              flexDirection: 'column',
            },
            body: {
              paddingLeft: 54,
              paddingRight: 54,
            },
          }}
        >
          {selectedEventId &&
            (pastOrUpcoming === 'UPCOMING' ? (
              <EventDetailPanel selectedEvent={selectedEvent} onCloseEvent={handleUnselectEvent} />
            ) : (
              <MeetingDetailsContent
                eventId={selectedEventId}
                onDelete={() => {}}
                basePath={`/client/${clientId}/meetings/past/${selectedEventId}`}
                onClose={handleUnselectEvent}
                tab={eventTab}
              />
            ))}
        </Drawer>
        <PageWithSidebar
          links={DATA_LINKS}
          activeUrl={`/client/${clientId}/meetings/${sectionSlug}`}
        >
          {content}
        </PageWithSidebar>
      </Box>
    </Flex>
  );
}
